import { createElement, useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { getRevealObserver } from './choreography.js';

type RevealVariant = 'up' | 'left' | 'right' | 'scale' | 'mask' | 'fade';
type RevealTag =
  'div' | 'section' | 'header' | 'article' | 'h2' | 'h3' | 'p' | 'span' | 'ul' | 'li';

export interface RevealProps {
  /** Element to render (default div). */
  as?: RevealTag;
  /** Motion flavour — `mask` lets a nested <MaskedReveal> drive the rise. */
  variant?: RevealVariant;
  /** Stagger index — child N waits N × step (set on a group of siblings). */
  i?: number;
  // `| undefined` is explicit because CSS-module classes type as `string |
  // undefined` and the package compiles with exactOptionalPropertyTypes.
  className?: string | undefined;
  id?: string | undefined;
  style?: CSSProperties | undefined;
  'aria-label'?: string | undefined;
  'aria-hidden'?: boolean | undefined;
  role?: string | undefined;
  children?: ReactNode;
}

/**
 * Declarative scroll reveal. Registers its own node with the shared observer
 * (see choreography.ts) on mount and tears down on unmount — so it works for
 * statically-composed sections and dynamically-rendered content alike, with no
 * per-section hook. The actual hide/show is pure CSS keyed off `[data-reveal]`
 * / `[data-in]` (motion.css), and gated behind `html.js` so no-JS crawlers and
 * a failed bundle still see every word.
 */
export function Reveal({
  as = 'div',
  variant = 'up',
  i,
  className,
  style,
  children,
  ...rest
}: RevealProps) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = getRevealObserver();
    obs?.observe(el);
    return () => obs?.unobserve(el);
  }, []);

  const mergedStyle = i != null ? ({ ...style, '--i': i } as CSSProperties) : style;

  return createElement(
    as,
    { ref, 'data-reveal': variant, className, style: mergedStyle, ...rest },
    children,
  );
}
